import { DestroyRef, Directive, ElementRef, afterNextRender, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NgControl } from "@angular/forms";
import { comparable } from "./request/diff";

/** The controls of a field that differ from what the dialog opened with: the field shows while it has one. */
const changedControls = new WeakMap<HTMLElement, Set<NgControl>>();

/**
 * Marks the field of a control whose value differs from the one it had when the dialog showed it, and clears the
 * mark when the user puts that value back. The mark goes on the enclosing `.field`, or on the control itself.
 */
@Directive({ selector: "[formControlName], [appChanged]" })
export class ChangedFieldDirective {
    private readonly ngControl = inject(NgControl, { self: true });
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        // The control is bound once the view has been checked.
        afterNextRender(() => {
            const control = this.ngControl.control;
            if (!control) return;
            const initial = JSON.stringify(comparable(control.value));
            const target = this.host.closest<HTMLElement>(".field") ?? this.host;
            const update = () => {
                const changed = changedControls.get(target) ?? new Set<NgControl>();
                if (JSON.stringify(comparable(control.value)) !== initial) changed.add(this.ngControl);
                else changed.delete(this.ngControl);
                changedControls.set(target, changed);
                target.classList.toggle("changed", changed.size > 0);
            };
            control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(update);
            this.destroyRef.onDestroy(() => {
                changedControls.get(target)?.delete(this.ngControl);
                target.classList.toggle("changed", (changedControls.get(target)?.size ?? 0) > 0);
            });
        });
    }
}
